// userProfileData — slug resolution + data loaders for the /user/:slug and
// /u/:slug profile surface.
//
// WHY THIS FILE EXISTS (PROFILE-PAGE-NO-REHYDRATE-ON-SWITCH-1): these
// loaders used to be module-private inside `pages/userProfile.js`, so no
// other caller could re-run them. The workspace shell's PHASE-1 tenant wipe
// (workspace/scopes.js, bucket ⑦) drains `_profileViewed` + `_profileActivity`
// on every workspace switch, but a SAME-ROUTE switch never remounts the page,
// so `onCreate` — the only caller of the resolver — never ran again and the
// profile sat on its empty shell until a navigation.
//
// Moving the loaders here gives the switch a PHASE-2 pair, exactly like the
// chat / tickets / calendar / meet surfaces already have: the shell imports
// `hydrateUserProfile` from this `functions/` subpath (the proven cross-
// package import shape — see functions/teams.js) and re-runs the same
// hydration the page runs on mount. One resolver, two callers, no polling and
// no DOM reads.
//
// `hydrateUserProfile(root, opts)` is the single entry point:
//   opts.slug   — override the slug (default: read from the URL)
//   opts.write  — patch writer (default `root.update`). The shell passes its
//                 epoch-guarded `put` so a superseded switch cannot repaint.

import { getSDK } from './sdk.js'
import { listOrgMembers } from './listOrgMembers.js'
import { stripWorkspacePrefix } from './workspacePrefix.js'
import { workspaceApiBase } from './workspaceApiBase.js'

// Accepts both `/user/:slug` (canonical, used in shared links) and
// `/u/:slug` (compact alias). Both routes mount this page.
// The workspace-prefixed form (`/w/<org>/<ws>/user/<slug>`) is the SHIPPED
// URL shape on every workspace-scoped tab — the router matches the route on
// the stripped path, so the page mounted while this raw-pathname regex
// returned '' and the profile resolved to nobody (found live while verifying
// PROFILE-PAGE-NO-REHYDRATE-ON-SWITCH-1: `_profileSlug: ''`, `_profileViewed:
// null` on /w/system/hq/user/marina). Strip the prefix with the same helper
// the navbar/tabs use, so both URL shapes resolve identically.
const _slugFromPath = () => {
  if (typeof window === 'undefined') return ''
  const path = stripWorkspacePrefix(window.location.pathname) || '/'
  const m = path.match(/^\/(?:user|u)\/([^/?#]+)/)
  return m ? decodeURIComponent(m[1]) : ''
}

// Synchronous resolver — checks the local `peopleAll` directory. Returns
// the matched person or null. The async `_resolveViewedAsync` below
// extends this with a /core/auth/user?username=… lookup so canonical
// Mongo usernames (e.g. `nikoloza` for `nika@symbols.app`) resolve even
// though the Supabase `people` view doesn't carry the username column.
const _resolveLocal = (root, slug) => {
  if (!slug) return null
  const all = (root.peopleAll || []).concat(root.people || [])
  const byEmail = all.find((p) => (p.email || '').toLowerCase() === slug.toLowerCase())
  if (byEmail) return byEmail
  const bySlug = all.find((p) => p.slug === slug)
  if (bySlug) return bySlug
  const byPrefix = all.find((p) => ((p.email || '').split('@')[0] || '') === slug)
  if (byPrefix) return byPrefix
  return null
}

// Stamp an authoritative `_isSelf` marker on a resolved record when it is
// provably the signed-in user. The viewer identity (currentUserEmail /
// currentUserId) may not have hydrated yet, so we also match the slug against
// the current user's known username / email-prefix. Once stamped, the
// FollowBtn `hide` factory hides the control deterministically — independent
// of currentUser* timing (#self-follow).
const _markSelfIfMe = (root, rec, slug) => {
  if (!rec || rec._isSelf) return rec
  const meEmail = (root?.currentUserEmail || '').toLowerCase()
  const recEmail = (rec.email || '').toLowerCase()
  const meUser = (root?.currentUserUsername || '').toLowerCase()
  const meId = (root?.currentUserId || '').toString()
  const recId = (rec.userId || rec.id || rec._id || '').toString()
  const slugLc = (slug || '').toLowerCase()
  const isSelf =
    (!!meEmail && meEmail === recEmail) ||
    (!!meId && !!recId && meId === recId) ||
    (!!meUser && (meUser === slugLc || meUser === (recEmail.split('@')[0] || ''))) ||
    (!!meEmail && meEmail.split('@')[0] === slugLc)
  return isSelf ? { ...rec, _isSelf: true } : rec
}

// Build a self-record from the hydrated client identity. Used when the slug is
// provably the signed-in user but no people-directory row carries the canonical
// username (e.g. `nikoloza` for `nika@symbols.app`) — without this the resolver
// would fall through to the 404 fallback and `_notFound` would HIDE your own
// profile entirely (and, on the rare race where it did render, leave the Follow
// button without a self marker). Stamps `_isSelf` so the control hides
// deterministically regardless of currentUser* timing (#self-follow).
const _selfRecordIfSlugIsMe = (root, slug) => {
  const slugLc = (slug || '').toLowerCase()
  if (!slugLc) return null
  const meEmail = (root?.currentUserEmail || '').toLowerCase()
  const meUser = (root?.currentUserUsername || '').toLowerCase()
  const mePrefix = meEmail.split('@')[0] || ''
  const isMe =
    (!!meUser && meUser === slugLc) ||
    (!!mePrefix && mePrefix === slugLc) ||
    (!!meEmail && meEmail === slugLc)
  if (!isMe) return null
  return {
    email: root.currentUserEmail || '',
    name: root.currentUserName || root.currentUserUsername || slug,
    slug: root.currentUserUsername || slug,
    avatar: root.currentUserAvatar || '',
    userId: (root.currentUserId || '').toString(),
    _isSelf: true
  }
}

// PROFILE-PERSIST-1: merge the authoritative self-record (identity fields
// only — email/name/slug/avatar/userId/_isSelf, built from currentUser*
// client state) with the richer `_resolveLocal` row when the local directory
// ALSO has one for this person. `selfEarly` alone carries no bio/position/
// location/city/startDate, so returning it unconditionally (the pre-fix
// behavior of step 0 below) silently discarded those fields on every load of
// your own profile by username — even though `onCreate` awaits
// `listOrgMembers` (hydrating `peopleAll`) BEFORE ever calling
// `_resolveViewedAsync`, so the richer row is normally already available by
// the time this runs. Identity stays authoritative from `selfEarly` (spread
// last, wins on overlap); HR fields ride along from `local`.  `_resolveLocal`
// can legitimately return null before hydration (or for a genuinely
// directory-less account) — degrade to the plain self-record shape rather
// than throwing.
const _mergeSelfWithLocal = (root, slug, selfEarly) => {
  const local = _resolveLocal(root, slug)
  return local ? { ...local, ...selfEarly } : selfEarly
}

// SILENT-SAVE-FAIL-1 surface 4 (second half): the canonical-username self
// path below (the `getMe()` branch, reached when neither the slug-vs-
// currentUser* short-circuit NOR the local-directory lookup already matched)
// hand-built an identity-only record — email/name/slug/avatar/userId/
// _isSelf — with no bio/position/location/etc, same six-field shape
// `_mergeSelfWithLocal` already fixed for the OTHER self path. Mirrors that
// fix: merge the richer local-directory row in, keyed by the now-known
// email (the local directory keys on email/slug/email-prefix, never on the
// Mongo `username` this branch resolved by, so a slug-keyed lookup here
// would still miss even when the row exists).
const _mergeMeRecWithLocal = (root, slug, identity) => {
  const local = _resolveLocal(root, identity.email) || _resolveLocal(root, slug)
  return local ? { ...local, ...identity } : identity
}

const _resolveViewedAsync = async (root, slug) => {
  if (!slug) return null
  // 0. Authoritative self short-circuit — if the slug is the signed-in user's
  //    own username / email-prefix, resolve to SELF from hydrated client
  //    state, merged with the local directory row for HR fields (bio/
  //    position/location/startDate/etc — see _mergeSelfWithLocal above).
  //    Runs first so `/user/<my-username>` always renders your own profile
  //    with the self marker, even when the people directory lacks a username
  //    row and getMe() is slow/empty (the path that previously dead-ended at
  //    _notFound).
  const selfEarly = _selfRecordIfSlugIsMe(root, slug)
  if (selfEarly) return _mergeSelfWithLocal(root, slug, selfEarly)
  // 1. Local people-directory match (email, slug, prefix).
  const local = _resolveLocal(root, slug)
  if (local) return _markSelfIfMe(root, local, slug)
  // 2. Server lookup by Mongo username — `/core/auth/user?username=<slug>`.
  //    People view doesn't have the `username` column (it lives in Mongo
  //    `User.username`), so this is the only path that resolves canonical
  //    slugs like `nikoloza` for `nika@symbols.app`.
  try {
    const me = await getSDK().getMe?.()
    const meRec = me?.data?.user || me?.user || me
    if (meRec && (meRec.username === slug || meRec.email === slug)) {
      // The viewed person is the current user — surface from the cached
      // /core/auth/me record so the hero renders without an extra fetch.
      const identity = {
        email: meRec.email,
        name: meRec.name,
        slug: meRec.username,
        avatar: meRec.avatar,
        userId: meRec._id || meRec.id || '',
        // Authoritative self marker — getMe() proved the slug is the
        // signed-in user, so the Follow control hides even if
        // root.currentUserEmail / currentUserId haven't hydrated yet.
        // Closes the "follow yourself" gap (#self-follow).
        _isSelf: true
        // mark as "fully resolved" so the placeholder code below doesn't
        // flag it as not found while we wait for peopleAll
      }
      // SILENT-SAVE-FAIL-1 surface 4: merge in the local directory row (see
      // _mergeMeRecWithLocal) so bio/position/location/etc. survive on this
      // path too — without it, this was the second of two six-field
      // hand-built records that structurally could never carry the bio the
      // user just saved.
      return _mergeMeRecWithLocal(root, slug, identity)
    }
  } catch (_) {
    /* swallow — fall through to the server lookup */
  }
  try {
    // SDK first (WS-SDK-AUTH-USER-LOOKUP-METHOD-1): sdk.getUserByUsername
    // wraps GET /core/auth/user?username=, carries its own token through
    // TokenManager (no hand-read localStorage token), and unwraps the
    // envelope to the user object. Feature-detected because the registry
    // sdk (3.14.785) predates the method — consumers resolving a registry
    // copy take the raw-fetch fallback below until the next sdk publish,
    // and the fallback dies with that publish. When the method EXISTS it
    // owns this lookup outright: it hits the same endpoint, so re-fetching
    // on its miss would only repeat the same answer.
    const sdk = getSDK()
    if (typeof sdk?.getUserByUsername === 'function') {
      try {
        const u = await sdk.getUserByUsername(slug)
        if (u && (u.email || u.username)) {
          return {
            email: u.email,
            name: u.name,
            slug: u.username || slug,
            avatar: u.avatar
          }
        }
      } catch (_) {
        /* not found / auth error — fall through to the 404 marker */
      }
    } else {
      const tok =
        typeof window !== 'undefined' ? window.localStorage.getItem('symbols_access_token') : null
      if (tok) {
        // The canonical resolver — NOT a hand-rolled base. The old ternary
        // read the raw localStorage pin ('local' → literal localhost:8080,
        // anything else → window.location.origin, i.e. the PORTLESS PROXY on
        // dev hosts) and ignored ?channel= and SYMBOLS_API_CHANNEL entirely —
        // one of the "localhost:8080 error despite the override" call sites.
        const apiBase = workspaceApiBase()
        const resp = await fetch(`${apiBase}/core/auth/user?username=${encodeURIComponent(slug)}`, {
          headers: { Authorization: 'Bearer ' + tok }
        })
        if (resp.ok) {
          const body = await resp.json()
          const u = body?.data?.user || body?.user || body
          if (u && (u.email || u.username)) {
            return {
              email: u.email,
              name: u.name,
              slug: u.username || slug,
              avatar: u.avatar
            }
          }
        }
      }
    }
  } catch (_) {
    /* swallow */
  }
  // No match anywhere — return a not-found marker so the page renders
  // the 404 chrome. The page hides UserProfile and shows ProfileNotFound
  // based on this flag.
  return {
    email: slug.includes('@') ? slug : `${slug}@symbols.app`,
    name: slug.replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    slug,
    _notFound: true
  }
}

const _loadActivity = async (root, email, write) => {
  if (!email) return
  try {
    const since = new Date()
    since.setDate(since.getDate() - 365)
    const sinceDate = since.toISOString().slice(0, 10)
    const result = await getSDK()
      .execute('workspaceProject.activity', 'heatmap', {
        filter: {
          user_email: email,
          since: sinceDate,
          select: 'activity_type, score, occurred_on'
        }
      })
      .catch(() => null)
    const data = Array.isArray(result?.data) ? result.data : result?.data || result || []
    const counts = {}
    for (const row of data || []) {
      const day = row.occurred_on
      if (!day) continue
      if (!counts[day]) counts[day] = { score: 0, marker: 0, breakdown: {} }
      counts[day].score += row.score || 0
      counts[day].breakdown[row.activity_type] = (counts[day].breakdown[row.activity_type] || 0) + 1
      if (row.activity_type === 'standup') counts[day].marker++
    }
    write({ _profileActivity: counts })
  } catch (err) {
    console.warn('[userProfile/activity]', err?.message || err)
    write({ _profileActivity: {} })
  }
}

const _loadPublicProjects = async (root, slug, write) => {
  if (!slug) return
  try {
    const sdk = getSDK()
    if (!sdk?.listPublicProjects) return
    const rows = await sdk.listPublicProjects({ owner: slug, limit: 100 }).catch(() => null)
    const list = Array.isArray(rows) ? rows : rows?.data || []
    const projects = list.filter((p) => {
      const o = p.workspace?.organization?.slug || p.workspace?.slug || ''
      return o === slug
    })
    write({ _profileProjects: projects })
  } catch (err) {
    console.warn('[userProfile/projects]', err?.message || err)
    write({ _profileProjects: [] })
  }
}

// Full profile hydration — the exact sequence `pages/userProfile.js#onCreate`
// runs on mount, callable again on a same-route workspace switch.
//
// Sequence (unchanged from the original onCreate body):
//   1. raise `_profileLoading` and clear the previous tenant's rows BEFORE
//      any await, so neither the profile nor the 404 chrome renders a stale
//      or wrong identity during the round trip
//   2. hydrate the people directory when empty (`peopleAll`) — the local
//      resolver needs it
//   3. resolve the viewed record (local directory → getMe → username lookup)
//   4. fire the activity + public-projects loaders
export const hydrateUserProfile = async (root, opts = {}) => {
  if (!root) return null
  const write = typeof opts.write === 'function' ? opts.write : (patch) => root.update?.(patch)
  const slug = opts.slug != null ? opts.slug : _slugFromPath()

  write({
    _profileSlug: slug,
    _profileViewed: null,
    _profileLoading: true,
    _profileActivity: {},
    _profileProjects: null,
    _profileTab: root._profileTab || 'projects'
  })

  // Eagerly hydrate the people directory if it's empty. The local resolver
  // path (`_resolveLocal`) needs `peopleAll` to find people by email /
  // email-prefix; the server fallback handles canonical Mongo usernames.
  // On a workspace switch the roster is wiped and re-put by the shell, so
  // this is the re-fetch that makes the local match work again.
  const peopleLoaded = (root.peopleAll || []).length > 0 || (root.people || []).length > 0
  if (!peopleLoaded) {
    try {
      // Migrated workspaceProject.people → users.members. Args discarded
      // server-side; full member list returned.
      const list = await listOrgMembers(getSDK())
      if (list && list.length) write({ peopleAll: list })
    } catch (_) {
      // Best-effort; the async resolver below still falls through to
      // /core/auth/user?username=<slug>.
    }
  }

  const viewed = await _resolveViewedAsync(root, slug)
  write({ _profileViewed: viewed, _profileLoading: false })
  if (viewed?.email && !viewed._notFound) _loadActivity(root, viewed.email, write)
  if (slug && !viewed?._notFound) _loadPublicProjects(root, slug, write)
  return viewed
}

// True when the current URL is the profile surface. The shell uses it to
// decide whether a workspace switch must re-hydrate this page (the
// `*SurfaceActive` convention in loadPrivateData.js). Accepts an explicit
// path so the shell can pass its workspace-prefix-stripped one.
export const isUserProfilePath = (path) => {
  const p = path != null ? path : typeof window !== 'undefined' ? window.location.pathname : ''
  return /^\/(?:user|u)\/[^/?#]+/.test(p || '')
}

// Test-only surface — pure-logic seam for the slug-resolution helpers.
// `resolveViewedAsync` never touches getSDK()/fetch on the `selfEarly`
// short-circuit path the tests exercise.
export const __userProfileInternals = {
  slugFromPath: _slugFromPath,
  resolveLocal: _resolveLocal,
  markSelfIfMe: _markSelfIfMe,
  selfRecordIfSlugIsMe: _selfRecordIfSlugIsMe,
  mergeSelfWithLocal: _mergeSelfWithLocal,
  mergeMeRecWithLocal: _mergeMeRecWithLocal,
  resolveViewedAsync: _resolveViewedAsync
}
